import { useEffect } from "react";
import { MicIcon, RefreshIcon, SpinnerIcon, StopIcon, TrashIcon } from "@/components/icons";
import { formatDuration } from "./format";
import { VOICE_MAX_SECONDS } from "./limits";
import { useBeforeUnloadWarning } from "./useBeforeUnloadWarning";
import { useVoiceRecorder } from "./useVoiceRecorder";

/** A stopped recording a host holds until it can send it. */
export interface VoiceRecording {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
}

interface VoiceRecorderProps {
  /** Without it there is no Send: the host holds the clip through onRecordedChange and sends it later. */
  onSend?: (blob: Blob, mimeType: string, durationSeconds: number) => void;
  maxSeconds?: number;
  /** True while a recording is running or recorded but not sent, false otherwise and on unmount. */
  onUnsentChange?: (hasUnsent: boolean) => void;
  /** The stopped clip while it waits for review, null otherwise and on unmount. */
  onRecordedChange?: (recording: VoiceRecording | null) => void;
}

/** Record, replay, re-record or delete, then send (DD-046). Knows nothing about uploads. */
export function VoiceRecorder({
  onSend,
  maxSeconds = VOICE_MAX_SECONDS,
  onUnsentChange,
  onRecordedChange,
}: VoiceRecorderProps) {
  const { state, clip, start, stop, reset } = useVoiceRecorder(maxSeconds);
  const hasUnsent = state.status === "recording" || state.status === "recorded";

  useEffect(() => {
    if (!onUnsentChange) return undefined;
    onUnsentChange(hasUnsent);
    return () => onUnsentChange(false);
  }, [hasUnsent, onUnsentChange]);

  const recorded = state.status === "recorded" ? clip : null;
  useEffect(() => {
    if (!onRecordedChange) return undefined;
    onRecordedChange(
      recorded ? { blob: recorded.blob, mimeType: recorded.mimeType, durationSeconds: recorded.durationSeconds } : null,
    );
    return () => onRecordedChange(null);
  }, [recorded, onRecordedChange]);

  // Closing the tab or reloading would drop the recording: ask first.
  useBeforeUnloadWarning(hasUnsent);

  if (state.status === "unsupported") {
    return <p className="text-sm text-gray-600">Recording isn't supported in this browser.</p>;
  }

  if (state.status === "denied") {
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">
          Microphone access is blocked. Allow it in your browser settings, then try again.
        </p>
        <button
          type="button"
          onClick={() => void start()}
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (state.status === "requesting") {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <SpinnerIcon className="w-4 h-4" />
        Waiting for the microphone…
      </div>
    );
  }

  if (state.status === "recording") {
    return (
      <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2">
        <span className="flex items-center gap-2 text-sm font-medium text-red-700">
          <span className="h-2 w-2 rounded-full bg-red-600" />
          Recording {formatDuration(state.elapsedSeconds)}
        </span>
        <button
          type="button"
          onClick={stop}
          className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          <StopIcon className="w-4 h-4" />
          Stop
        </button>
      </div>
    );
  }

  if (state.status === "recorded" && clip) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <audio controls src={clip.url} className="min-w-0 flex-1" />
          <span className="shrink-0 text-sm tabular-nums text-gray-600">{formatDuration(clip.durationSeconds)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                reset();
                void start();
              }}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <RefreshIcon className="w-4 h-4" />
              Re-record
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
          {onSend && (
            <button
              type="button"
              onClick={() => onSend(clip.blob, clip.mimeType, clip.durationSeconds)}
              className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
            >
              Send
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void start()}
      className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-6 text-gray-600 hover:border-blue-400 hover:text-blue-600"
    >
      <MicIcon className="w-8 h-8" />
      <span className="text-sm font-medium">Tap to record</span>
      <span className="text-xs text-gray-400">Up to {Math.round(maxSeconds / 60)} min</span>
    </button>
  );
}
